import * as React from 'react';

import { NetworkStatus } from 'apollo-client';
import type { ApolloError } from 'apollo-client';

import type { QueryGetSimilarityChecksByIdQuery } from 'bundles/academic-integrity/components/plagiarism-detection/api/__generated__/QueryGetSimilarityChecksById';
import type { QueryGetSimilarityChecksByMetadataQuery } from 'bundles/academic-integrity/components/plagiarism-detection/api/__generated__/QueryGetSimilarityChecksByMetadata';
import {
  useGetSimilarityChecksById,
  useGetSimilarityChecksByMetadata,
  useSimilarityCheckSubmit,
} from 'bundles/academic-integrity/components/plagiarism-detection/api/similarityChecksApis';

/* eslint-enable graphql/template-strings */

export const SimilarityChecksStatuses = {
  COMPLETED: 'COMPLETED',
  IN_PROGRESS: 'IN_PROGRESS',
  FAILED: 'FAILED',
} as const;

export type SimilarityChecksStatus = (typeof SimilarityChecksStatuses)[keyof typeof SimilarityChecksStatuses];

export type SimilarityChecksErrors =
  | 'UNSUPPORTED_FILETYPE'
  | 'PROCESSING_ERROR'
  | 'TOO_LITTLE_TEXT'
  | 'TOO_MUCH_TEXT'
  | 'CANNOT_EXTRACT_TEXT'
  | 'TOO_MANY_PAGES'
  | 'FILE_LOCKED'
  | 'CORRUPT_FILE'
  // 'ZERO_LENGTH_FILE' networkError added manually not from graphql
  | 'ZERO_LENGTH_FILE';

export type SimilarityChecksResult = {
  orgCourseraSimilaritycheckSimilarityCheckSuccess?: {
    score: number;
    url: string;
  };
  orgCourseraSimilaritycheckSimilarityCheckFailure?: {
    errorMsg: SimilarityChecksErrors;
  };
};

export type SimilarityChecksData = {
  status: SimilarityChecksStatus;
  result?: SimilarityChecksResult;
  elements?: Array<{ id: string }>;
};

type Props = {
  courseId: string;
  itemId: string;
  authorId: number;
  questionResponseId: string;
  scopedTeamId?: string;
  skip: boolean;
  children: (x0: {
    similarityChecksData?: SimilarityChecksData;
    loading: boolean;
    refetchReport: () => Promise<{}>;
    submitForCheck: () => Promise<{}>;
    error?: ApolloError;
    hasNetworkError: boolean;
  }) => React.ReactElement | null;
  useDraft: boolean;
  onSimilarityChecksDataLoaded?: (data?: SimilarityChecksData) => void;
};

/**
 * Parses the GraphQL API response data for similarity checks into a standardized format
 */
const parseGraphQlAPIResponseData = (
  data: QueryGetSimilarityChecksByIdQuery | undefined
): SimilarityChecksData | undefined => {
  if (!data) {
    return undefined;
  }
  if (data?.SimilarityChecks?.queryByCheckId?.result?.__typename === 'SimilarityCheck_InProgressStatus') {
    return {
      status: 'IN_PROGRESS',
    };
  }

  if (data?.SimilarityChecks?.queryByCheckId?.result?.__typename === 'SimilarityCheck_FailedStatus') {
    return {
      status: 'FAILED',
      result: {
        orgCourseraSimilaritycheckSimilarityCheckFailure: {
          errorMsg: data?.SimilarityChecks?.queryByCheckId?.result?.error as SimilarityChecksErrors,
        },
      },
    };
  }
  const similarityChecksData: SimilarityChecksData = {
    status: 'COMPLETED',
    result: {
      orgCourseraSimilaritycheckSimilarityCheckSuccess: {
        score: data?.SimilarityChecks?.queryByCheckId?.result?.score || 0,
        url: data?.SimilarityChecks?.queryByCheckId?.result?.reportUrl || '',
      },
    },
    elements: [
      {
        id: data?.SimilarityChecks?.queryByCheckId?.similarityCheckId || '',
      },
    ],
  };

  return similarityChecksData;
};

/**
 * Parses the GraphQL API response data for similarity checks into a standardized format
 */
const parseGraphQlResponseData = (
  data: QueryGetSimilarityChecksByMetadataQuery | undefined
): SimilarityChecksData | undefined => {
  if (!data) {
    return undefined;
  }
  if (data?.SimilarityChecks?.queryByMetadata?.result?.__typename === 'SimilarityCheck_InProgressStatus') {
    return {
      status: SimilarityChecksStatuses.IN_PROGRESS,
    };
  }

  if (data?.SimilarityChecks?.queryByMetadata?.result?.__typename === 'SimilarityCheck_FailedStatus') {
    return {
      status: SimilarityChecksStatuses.FAILED,
      result: {
        orgCourseraSimilaritycheckSimilarityCheckFailure: {
          errorMsg: data?.SimilarityChecks?.queryByMetadata?.result?.error as SimilarityChecksErrors,
        },
      },
    };
  }
  const similarityChecksData: SimilarityChecksData = {
    status: SimilarityChecksStatuses.COMPLETED,
    result: {
      orgCourseraSimilaritycheckSimilarityCheckSuccess: {
        score: data?.SimilarityChecks?.queryByMetadata?.result?.score || 0,
        url: data?.SimilarityChecks?.queryByMetadata?.result?.reportUrl || '',
      },
    },
    elements: [
      {
        id: data?.SimilarityChecks?.queryByMetadata?.similarityCheckId || '',
      },
    ],
  };

  return similarityChecksData;
};

const SimilarityChecksGraphQL = ({
  courseId,
  itemId,
  authorId,
  questionResponseId,
  skip,
  children,
  useDraft,
  onSimilarityChecksDataLoaded,
  scopedTeamId,
}: Props) => {
  const submitterId = `${authorId}`;
  const variables = {
    courseId,
    itemId,
    responseId: questionResponseId,
    considerDrafts: useDraft,
    submitter: scopedTeamId
      ? {
          teamSubmitter: {
            submitterId,
            scopedTeamId,
          },
        }
      : {
          individualSubmitter: {
            submitterId,
          },
        },
  };

  const [submitSimilarityCheck] = useSimilarityCheckSubmit();

  const { data, loading, error, networkStatus, refetch } = useGetSimilarityChecksByMetadata(variables, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
    onCompleted: (onCompletedData) => {
      const similarityChecksData = parseGraphQlResponseData(onCompletedData);
      if (onSimilarityChecksDataLoaded && similarityChecksData) {
        onSimilarityChecksDataLoaded(similarityChecksData);
      }
    },
    skip,
  });

  const hasNetworkError = networkStatus === NetworkStatus.error;
  const submitForCheck = () => {
    return submitSimilarityCheck({
      variables: {
        input: variables,
      },
    });
  };
  const refetchReport = () => {
    return refetch({ metadata: variables });
  };
  const similarityChecksData = parseGraphQlResponseData(data);
  if (loading || !similarityChecksData) {
    return children({ refetchReport, submitForCheck, loading, error, hasNetworkError });
  }

  return children({
    similarityChecksData,
    refetchReport,
    submitForCheck,
    loading,
    error,
    hasNetworkError,
  });
};

type SimilarityChecksWithIdProps = {
  id: string;
  children: (x0: {
    similarityChecksData?: SimilarityChecksData;
    loading: boolean;
    refetchReport: () => Promise<{}>;
  }) => React.ReactElement | null;
};

const SimilarityChecksWithIdGraphQL = ({ id, children }: SimilarityChecksWithIdProps) => {
  const { data, loading, refetch } = useGetSimilarityChecksById(id, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });
  const similarityChecksData = parseGraphQlAPIResponseData(data);
  return (
    <>
      {children({
        similarityChecksData,
        refetchReport: refetch,
        loading,
      })}
    </>
  );
};

export const SimilarityChecksWithId = (props: SimilarityChecksWithIdProps) => {
  return <SimilarityChecksWithIdGraphQL {...props} />;
};

export const SimilarityChecks = (props: Props) => {
  return <SimilarityChecksGraphQL {...props} />;
};

export default React.memo(SimilarityChecks);
